import { plural, t } from "@lingui/core/macro";
import React from "react";
import DropDownFilter from "../DropDownFilter/DropDownFilter";
import MonthCalendar from "components/MonthCalendar";
import { FirstLastDepartureCruiseDates } from "types/graphql-types";
import { useCurrentRefinements } from "react-instantsearch";
import RefinedListWrapper from "../RefinementListFilter/RefinementListWrapper";
import { CurrentRefinementsConnectorParamsRefinement } from "instantsearch.js/es/connectors/current-refinements/connectCurrentRefinements";
import { getStartDate } from "components/MonthCalendar/utils";
import { Language } from "src/utils/localization/languages/types";
import { formatDate } from "src/utils/date";
import { useLanguage } from "src/utils/localization/languages/I18nContext";
import { arrayIsEmpty } from "src/utils/checks";
import { DynamicTitleFunction } from "../RefinementListFilter/RefinedListDropdown";
import { DEFAULT_SEPARATOR } from "components/FindYourCruise/Components/RefinementListFilter/label";
import * as styles from "./DepartureDateFilter.module.scss";
import clsx from "clsx";

export const DepartureDateFilterDropDown: React.FC<{
  firstLastDepartureCruiseDates: FirstLastDepartureCruiseDates;
  backgroundColor?: "white" | "neutral05" | "titanium-dark";
  contentWrapperClassName?: string;
  emptyLabelPlaceholder?: string;
}> = ({
  firstLastDepartureCruiseDates,
  backgroundColor = "neutral05",
  contentWrapperClassName,
  emptyLabelPlaceholder
}) => {
  const language = useLanguage();
  const { items } = useCurrentRefinements({
    includedAttributes: ["departureYearMonth"]
  });
  const appliedDepartureYearMonthFilterCount =
    items?.[0]?.refinements?.length || 0;

  return (
    <DropDownFilter
      label={
        items?.[0]?.refinements.length > 0 || !emptyLabelPlaceholder
          ? t`Which period?`
          : emptyLabelPlaceholder
      }
      selectedValuesLabel={buildSelectedValuesLabel(
        items?.[0]?.refinements,
        language
      )}
      dataContentPiece="date_filter_interaction"
      title={buildDynamicTitle(appliedDepartureYearMonthFilterCount)}
      className={clsx({
        [styles.dateDropdown]: backgroundColor == "white",
        [styles.filledBackgroundColor]:
          items?.[0]?.refinements?.length > 0 &&
          backgroundColor !== "titanium-dark"
      })}
      contentWrapperClassName={contentWrapperClassName}
      variant={backgroundColor == "titanium-dark" ? "dark" : "default"}
    >
      <MonthCalendar
        id="dropdown"
        operator="or"
        attribute="departureYearMonth"
        limit={100}
        minYearMonth={firstLastDepartureCruiseDates.minYearMonth}
        maxYearMonth={firstLastDepartureCruiseDates.maxYearMonth}
        variant={backgroundColor == "titanium-dark" ? "dark" : "default"}
      />
    </DropDownFilter>
  );
};

export const DepartureDateFilterList: React.FC<{
  firstLastDepartureCruiseDates: FirstLastDepartureCruiseDates;
  id: string;
}> = ({ id, firstLastDepartureCruiseDates }) => {
  const { items } = useCurrentRefinements({
    includedAttributes: ["departureYearMonth"]
  });
  const count = items.flatMap((x) => x.refinements).length;

  return (
    <RefinedListWrapper
      title={buildDynamicTitle(count)}
      dataContentName="date_filter_interaction"
    >
      <MonthCalendar
        id={id}
        operator="or"
        attribute="departureYearMonth"
        limit={100}
        minYearMonth={firstLastDepartureCruiseDates.minYearMonth}
        maxYearMonth={firstLastDepartureCruiseDates.maxYearMonth}
      />
    </RefinedListWrapper>
  );
};

const buildDynamicTitle: DynamicTitleFunction = (count) =>
  count > 0
    ? plural(count, {
        one: "# departure month selected",
        other: "# departure months selected"
      })
    : t`Set your departure dates`;

type CompactedDate = { date: Date; renderYear: boolean };
const compactYears = (dates: Date[]): CompactedDate[] => {
  return dates
    .sort((a, b) => a.getTime() - b.getTime())
    .reduceRight((acc, date) => {
      const lastItem = acc[acc.length - 1];
      if (lastItem) {
        const sameYear = lastItem.date.getFullYear() == date.getFullYear();
        acc.push({ date: date, renderYear: !sameYear });
      } else {
        acc.push({ date: date, renderYear: true });
      }

      return acc;
    }, [] as CompactedDate[])
    .reverse();
};

export const buildSelectedValuesLabel = (
  refinements: CurrentRefinementsConnectorParamsRefinement[] | undefined = [],
  language: Language
) => {
  const dates = refinements.map((r) => getStartDate(r.value.toString()));
  if (arrayIsEmpty(dates)) return undefined;

  return compactYears(dates)
    .map((r) =>
      formatDate(r.date, language.ogLang, {
        year: r.renderYear ? "numeric" : undefined,
        month: "short"
      })
    )
    .join(DEFAULT_SEPARATOR);
};
